import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/Button';
import { IconClose } from '@/components/Icons';

const ACCEPT = 'image/png,image/jpeg,image/webp';
const MAX_BYTES = 2 * 1024 * 1024;

export interface LogoPickerProps {
  value: File | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
  error?: string;
}

/**
 * A file picker with a local preview, for choosing a logo BEFORE the tenant
 * exists — no network call happens here. `object-URL` previews are revoked on
 * every change/unmount; leaving them would leak memory for the lifetime of
 * the tab, since nothing else ever references them.
 */
export function LogoPicker({ value, onChange, disabled, error }: LogoPickerProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!value) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(value);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [value]);

  const handleFile = (file: File | undefined) => {
    setLocalError(null);
    if (!file) return;
    if (file.size > MAX_BYTES) {
      setLocalError(`Image is too large — the limit is ${MAX_BYTES / (1024 * 1024)}MB.`);
      return;
    }
    onChange(file);
  };

  return (
    <div>
      <label className="label" htmlFor="logo">
        Restaurant logo
      </label>
      <div className="mt-2 flex items-start gap-4">
        <span
          aria-hidden="true"
          className="flex h-32 w-32 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-400"
        >
          {previewUrl ? (
            <img src={previewUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            'No logo'
          )}
        </span>
        <div className="flex flex-col items-start gap-2 pt-1">
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={disabled}
            >
              {value ? 'Replace' : 'Choose image'}
            </Button>
            {value ? (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                icon={<IconClose className="h-3.5 w-3.5" />}
                onClick={() => {
                  onChange(null);
                  if (inputRef.current) inputRef.current.value = '';
                }}
                disabled={disabled}
              >
                Remove
              </Button>
            ) : null}
          </div>
          <p className="hint">Optional. PNG, JPEG or WebP, up to 2MB.</p>
        </div>
        <input
          ref={inputRef}
          id="logo"
          type="file"
          accept={ACCEPT}
          className="sr-only"
          disabled={disabled}
          onChange={(event) => handleFile(event.target.files?.[0])}
        />
      </div>
      {localError || error ? <p className="field-error mt-1">{localError ?? error}</p> : null}
    </div>
  );
}
